import type { Seconds } from "./units.js";


// Pre-compiled regexes for efficiency
const SIBILANT_REGEX = /s$|ss$|sh$|ch$|x$|z$/;
const VOWEL_S_REGEX = /as$|es$|is$|os$|us$/;
const CONSONANT_Y_REGEX = /[b-df-hj-np-tv-z]y$/;
const F_FE_REGEX = /f$|fe$/;
const CONSONANT_O_REGEX = /[b-df-hj-np-tv-z]o$/;

export function plural(nonPlural: string, count: number) {
    // Return singular unchanged when count === 1 or when empty
    if (count === 1 || nonPlural === "")
        return nonPlural;

    // 1. Words ending in sibilant sounds
    if (SIBILANT_REGEX.test(nonPlural)) {
        return `${nonPlural}es`;
    }

    // 2. Words ending in single "s" preceded by a vowel
    if (VOWEL_S_REGEX.test(nonPlural)) {
        return `${nonPlural}es`;
    }

    // 3. Consonant + "y"
    if (CONSONANT_Y_REGEX.test(nonPlural)) {
        return `${nonPlural.slice(0, -1)}ies`;
    }

    // 4. Words ending in "f" or "fe"
    if (F_FE_REGEX.test(nonPlural)) {
        const base = nonPlural.endsWith("fe") ? nonPlural.slice(0, -2) : nonPlural.slice(0, -1);
        return `${base}ves`;
    }

    // 5. Words ending in single "o" preceded by a consonant
    if (CONSONANT_O_REGEX.test(nonPlural)) {
        return `${nonPlural}es`;
    }

    // 6. Default: append "s"
    return `${nonPlural}s`;
}

export function digitInEnglish(value: number): string {
    const english = [
        "Zero",
        "One",
        "Two",
        "Three",
        "Four",
        "Five",
        "Six",
        "Seven",
        "Eight",
        "Nine",
    ][value];
    if (english === undefined)
        throw new Error("Unknown digit " + value);

    return english;
}


export interface FormatDateOptions {
    verb?: string;
}


export function formatDate(
    date: Date,
    options?: FormatDateOptions,
    scheduleRecalc?: (seconds: Seconds) => void,
): string {

    scheduleRecalc ??= () => {};

    let verbPrefix = (options?.verb ? `${options.verb} ` : "");

    const currentTime = new Date();
    const rawSeconds = (currentTime.getTime() - date.getTime()) / 1000;
    const seconds = Math.abs(rawSeconds);
    const inFuture = (rawSeconds < 0);

    const minute = 60;
    const minutes = Math.floor(seconds / minute);
    if (minutes <= 60) {
        scheduleRecalc(minute - seconds % minute);
        if (inFuture) {
            if (minutes <= 0)
                return `${verbPrefix}in less than a minute`;

            return `${verbPrefix}in ${Math.floor(minutes)} ${plural("minute", minutes)}`;
        } else {
            if (minutes <= 0)
                return `${verbPrefix}less than a minute ago`;

            return `${verbPrefix}${Math.floor(minutes)} ${plural("minute", minutes)} ago`;
        }
    }

    const hour = 60 * minute;
    const hours = Math.floor(seconds / hour);
    if (hours <= 24) {
        scheduleRecalc(hour - seconds % hour);
        if (inFuture) {
            return `${verbPrefix}in ${hours} ${plural("hour", hours)}`;
        } else {
            return `${verbPrefix}${hours} ${plural("hour", hours)} ago`;
        }
    }

    if (inFuture || verbPrefix !== "") {
        verbPrefix += "on ";
    }
    return verbPrefix + date.toLocaleDateString(
        "en-GB", {
            year: "numeric", month: "long", day: "numeric",
        },
    );
}


export function formatLocaleDate(iso: string): string {
    return new Date(iso).toLocaleDateString();
}


export function formatInt(value: number): string {
    return value.toLocaleString("en-US");
}


const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];


export function formatMonth(month: number): string {
    return months[month];
}

export function formatRatingChange(ratingChange: number): string {
    return (ratingChange >= 0 ? "+" : "-") + `${Math.abs(ratingChange).toFixed(0)}`;
}
